import type { FC } from "react";
import { useAtom } from "jotai";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { Button } from "@/components/flexkit/Button";
import { Select } from "@/components/flexkit/select/Select";
import { Alert, AlertDescription } from "@/components/podkit/alert/Alert";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { v4 as uuidv4 } from "uuid";
import type {
    WorkflowEditFormReportOutputData,
    ReportOutputSchemaType,
    ReportOutputExtractionType,
} from "@/components/automations/automation-edit-form-data";
import type { DraftReportStep } from "@/routes/automations/automation-edit-page/types";
import { withGitpodSize } from "@/assets/icons/utils";
import IconColumnWideAdd from "central-icons/IconColumnWideAdd";
import IconTrashCan from "central-icons/IconTrashCan";

const IconColumnWideAddWithSize = withGitpodSize(IconColumnWideAdd);
const IconTrashCanWithSize = withGitpodSize(IconTrashCan);

export const AutomationReportStepEditor: FC<{ draft: DraftReportStep }> = ({ draft }) => {
    const [outputs, setOutputs] = useAtom(draft.outputs);

    const addOutput = () => {
        const newOutput: WorkflowEditFormReportOutputData = {
            id: uuidv4(),
            title: "",
            key: "",
            schemaType: "string",
            extractionType: "prompt",
            extractionValue: "",
        };
        setOutputs([...outputs, newOutput]);
    };

    const updateOutput = (index: number, updates: Partial<WorkflowEditFormReportOutputData>) => {
        const newOutputs = [...outputs];
        newOutputs[index] = { ...newOutputs[index], ...updates };
        setOutputs(newOutputs);
    };

    const removeOutput = (index: number) => {
        setOutputs(outputs.filter((_, i) => i !== index));
    };

    return (
        <div className="flex flex-col gap-4 px-6 pb-6">
            <Alert>
                <IconInfo size="sm" />
                <AlertDescription>
                    Define outputs that the agent should extract and report using the add_agent_execution_result tool.
                </AlertDescription>
            </Alert>

            {outputs.map((output, index) => (
                <OutputEditor
                    key={output.id}
                    output={output}
                    index={index}
                    onUpdate={(updates) => updateOutput(index, updates)}
                    onRemove={() => removeOutput(index)}
                />
            ))}

            <div className="flex items-center gap-2">
                <Button
                    variant="secondary"
                    LeadingIcon={IconColumnWideAddWithSize}
                    onClick={addOutput}
                    className="w-fit"
                    data-tracking-id="add-output"
                >
                    Add output
                </Button>
                <hr className="h-[1.5px] flex-1 rounded-full border-0 bg-border-strong" />
            </div>
        </div>
    );
};

const OutputEditor: FC<{
    output: WorkflowEditFormReportOutputData;
    index: number;
    onUpdate: (updates: Partial<WorkflowEditFormReportOutputData>) => void;
    onRemove: () => void;
}> = ({ output, index, onUpdate, onRemove }) => {
    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-base bg-surface-secondary p-4">
            <div className="flex items-center justify-between">
                <span className="text-base font-medium text-content-primary">Output {index + 1}</span>
                <Button
                    variant="ghost"
                    size="sm"
                    LeadingIcon={IconTrashCanWithSize}
                    onClick={onRemove}
                    data-tracking-id="remove-output"
                />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <InputField label="Title">
                    <Input
                        className="w-full"
                        type="text"
                        value={output.title}
                        placeholder="e.g., Test Coverage"
                        onChange={(e) => onUpdate({ title: e.target.value })}
                    />
                </InputField>

                <InputField label="Key">
                    <Input
                        className="w-full"
                        type="text"
                        value={output.key}
                        placeholder="e.g., test_coverage"
                        onChange={(e) => onUpdate({ key: e.target.value })}
                    />
                </InputField>
            </div>

            <div className="grid grid-cols-2 gap-3">
                <InputField label="Value type">
                    <Select
                        value={output.schemaType}
                        onValueChange={(value) => onUpdate({ schemaType: value as ReportOutputSchemaType })}
                        className="w-full"
                    >
                        <Select.Item value="string">String</Select.Item>
                        <Select.Item value="integer">Integer</Select.Item>
                        <Select.Item value="float">Float</Select.Item>
                        <Select.Item value="boolean">Boolean</Select.Item>
                    </Select>
                </InputField>

                <InputField label="Extraction method">
                    <Select
                        value={output.extractionType}
                        onValueChange={(value) => onUpdate({ extractionType: value as ReportOutputExtractionType })}
                        className="w-full"
                    >
                        <Select.Item value="prompt">Prompt</Select.Item>
                        <Select.Item value="command">Command</Select.Item>
                    </Select>
                </InputField>
            </div>

            <SchemaConstraints output={output} onUpdate={onUpdate} />

            <InputField label={output.extractionType === "prompt" ? "Extraction instructions" : "Extraction command"}>
                <Textarea
                    value={output.extractionValue}
                    placeholder={
                        output.extractionType === "prompt"
                            ? "Instructions for extracting this value..."
                            : "Shell command to extract the value..."
                    }
                    onChange={(e) => onUpdate({ extractionValue: e.target.value })}
                    className="resize-none"
                    minHeight={80}
                    maxHeight={120}
                />
            </InputField>

            <InputField label="Acceptance criteria (optional)">
                <Input
                    className="w-full"
                    type="text"
                    value={output.acceptanceCriteria || ""}
                    placeholder="e.g., value >= 80"
                    onChange={(e) => onUpdate({ acceptanceCriteria: e.target.value || undefined })}
                />
            </InputField>
        </div>
    );
};

const SchemaConstraints: FC<{
    output: WorkflowEditFormReportOutputData;
    onUpdate: (updates: Partial<WorkflowEditFormReportOutputData>) => void;
}> = ({ output, onUpdate }) => {
    switch (output.schemaType) {
        case "string":
            return (
                <InputField label="Pattern (optional regex)">
                    <Input
                        className="w-full"
                        type="text"
                        value={output.stringPattern || ""}
                        placeholder="e.g., ^v[0-9]+\\.[0-9]+\\.[0-9]+$"
                        onChange={(e) => onUpdate({ stringPattern: e.target.value || undefined })}
                    />
                </InputField>
            );
        case "integer":
            return (
                <div className="grid grid-cols-2 gap-3">
                    <InputField label="Min (optional)">
                        <Input
                            className="w-full"
                            type="number"
                            value={output.integerMin ?? ""}
                            placeholder="Min value"
                            onChange={(e) =>
                                onUpdate({
                                    integerMin: e.target.value ? Number.parseInt(e.target.value, 10) : undefined,
                                })
                            }
                        />
                    </InputField>
                    <InputField label="Max (optional)">
                        <Input
                            className="w-full"
                            type="number"
                            value={output.integerMax ?? ""}
                            placeholder="Max value"
                            onChange={(e) =>
                                onUpdate({
                                    integerMax: e.target.value ? Number.parseInt(e.target.value, 10) : undefined,
                                })
                            }
                        />
                    </InputField>
                </div>
            );
        case "float":
            return (
                <div className="grid grid-cols-2 gap-3">
                    <InputField label="Min (optional)">
                        <Input
                            className="w-full"
                            type="number"
                            step="0.01"
                            value={output.floatMin ?? ""}
                            placeholder="Min value"
                            onChange={(e) =>
                                onUpdate({ floatMin: e.target.value ? Number.parseFloat(e.target.value) : undefined })
                            }
                        />
                    </InputField>
                    <InputField label="Max (optional)">
                        <Input
                            className="w-full"
                            type="number"
                            step="0.01"
                            value={output.floatMax ?? ""}
                            placeholder="Max value"
                            onChange={(e) =>
                                onUpdate({ floatMax: e.target.value ? Number.parseFloat(e.target.value) : undefined })
                            }
                        />
                    </InputField>
                </div>
            );
        case "boolean":
            return null;
        default:
            return null;
    }
};
